import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useSocket } from '../../contexts/SocketContext';
import { useDropRunState } from '../../hooks/useDropRunState';
import { forceValidateTier } from '../../api/drops';
import { fetchBrand } from '../../api/brands';
import { computeVerdict } from '../../lib/verdictEngine';
import axios from 'axios';

const ACTIVE_RUN_STATUSES = new Set(['live', 'funding', 'awaiting_production', 'active']);
const ELIGIBLE_PLEDGE_STATUSES = new Set(['authorized', 'captured']);

const FORCE_REASON_OPTIONS = [
  { value: 'strong_demand', label: 'Strong demand signal despite target miss' },
  { value: 'strategic', label: 'Strategic / marketing drop' },
  { value: 'inventory_commitment', label: 'Carryover inventory commitment' },
  { value: 'customer_promise', label: 'Customer promise / pre-sold' },
  { value: 'other', label: 'Other (describe below)' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const formatCountdown = (deadlineIso) => {
  if (!deadlineIso) return 'No deadline';
  const deadline = new Date(deadlineIso).getTime();
  if (!Number.isFinite(deadline)) return 'No deadline';
  const delta = deadline - Date.now();
  if (delta <= 0) return 'Expired';
  const days = Math.floor(delta / (24 * 60 * 60 * 1000));
  const hours = Math.floor((delta % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  return `${days}d ${hours}h remaining`;
};

const humanizeForceError = (error) => {
  const code = String(error?.response?.data?.code || '');
  if (code === 'reason_required') return 'A force reason is required below GO margin.';
  return String(error?.response?.data?.error || error?.message || 'Unable to force production right now.');
};

function StatCard({ title, value, hint }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

const DropDashboardPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { connected } = useSocket();
  const { brandId, campaignId } = useParams();

  const [tab, setTab] = useState('overview');
  const [showTransientError, setShowTransientError] = useState(false);
  const [forceModalTierId, setForceModalTierId] = useState('');
  const [forceReason, setForceReason] = useState('');
  const [forceReasonNote, setForceReasonNote] = useState('');
  const [forceSubmitting, setForceSubmitting] = useState(false);
  const [forceDialogError, setForceDialogError] = useState('');
  const [brandSlug, setBrandSlug] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBrandSlug(null);

    if (!brandId) return undefined;

    fetchBrand(brandId)
      .then((brand) => {
        if (!cancelled) setBrandSlug(brand?.slug || '');
      })
      .catch(() => {
        if (!cancelled) setBrandSlug('');
      });

    return () => {
      cancelled = true;
    };
  }, [brandId]);

  const { data, isLoading, isError, isFetching } = useDropRunState(campaignId, brandSlug, {
    enabled: Boolean(brandSlug),
  });

  useEffect(() => {
    if (!isError) {
      setShowTransientError(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowTransientError(true), 10000);
    return () => clearTimeout(timer);
  }, [isError]);

  const tiers = useMemo(() => (Array.isArray(data?.tiers) ? data.tiers : []), [data?.tiers]);
  const pledges = useMemo(() => (Array.isArray(data?.pledges) ? data.pledges : []), [data?.pledges]);
  const verdictConfig = data?.verdictConfig || {};
  const goMarginPct = Number(verdictConfig.goMarginPct ?? 35);
  const reviewMarginPct = Number(verdictConfig.reviewMarginPct ?? 20);

  const tierAliasMap = useMemo(() => {
    const map = new Map();
    tiers.forEach((tier) => {
      const aliases = Array.isArray(tier.aliases) && tier.aliases.length ? tier.aliases : [tier.tierId];
      aliases.forEach((alias) => map.set(String(alias), tier.tierId));
      map.set(String(tier.tierId), tier.tierId);
    });
    return map;
  }, [tiers]);

  const perTierMetrics = useMemo(() => {
    const rows = new Map(
      tiers.map((tier) => [
        tier.tierId,
        {
          tier,
          units: 0,
          revenue: 0,
          cogs: 0,
          margin: 0,
          carbonKg: null,
        },
      ])
    );

    pledges.forEach((pledge) => {
      if (!ELIGIBLE_PLEDGE_STATUSES.has(String(pledge.status || ''))) return;
      const canonicalTierId = tierAliasMap.get(String(pledge.tierId || '').trim());
      if (!canonicalTierId || !rows.has(canonicalTierId)) return;

      const row = rows.get(canonicalTierId);
      const qty = Math.max(1, Number(pledge.qty || 1));
      const wholesaleUnits = Boolean(pledge.isWholesale) ? Math.max(1, Number(row.tier.wholesaleUnitCount || 1)) : 1;
      const units = qty * wholesaleUnits;
      const fallbackUnitPrice = Boolean(pledge.isWholesale)
        ? Number(row.tier.wholesalePrice ?? row.tier.price ?? 0)
        : Number(row.tier.price || 0);
      const effectiveCents = Number(pledge.effectivePriceCents || pledge.amountCents || 0);
      const revenue = effectiveCents > 0 ? effectiveCents / 100 : fallbackUnitPrice * qty;

      row.units += units;
      row.revenue += revenue;
      row.cogs += units * Number(row.tier.costPerUnit || 0);
      row.margin = row.revenue > 0 ? ((row.revenue - row.cogs) / row.revenue) * 100 : 0;
      row.carbonKg =
        row.tier.carbonKgPerUnit == null ? null : Number((row.units * Number(row.tier.carbonKgPerUnit || 0)).toFixed(3));
    });

    return rows;
  }, [pledges, tierAliasMap, tiers]);

  const fundedRows = useMemo(
    () => Array.from(perTierMetrics.values()).filter(({ tier }) => tier.validated || tier.forcedProduction),
    [perTierMetrics]
  );

  const whatIfRows = useMemo(
    () =>
      Array.from(perTierMetrics.values()).filter(
        ({ tier, units }) => !tier.validated && !tier.forcedProduction && Number(units || 0) > 0
      ),
    [perTierMetrics]
  );

  const totals = useMemo(() => {
    const acc = { revenue: 0, cogs: 0, gp: 0 };
    fundedRows.forEach((row) => {
      acc.revenue += row.revenue;
      acc.cogs += row.cogs;
    });
    acc.gp = acc.revenue - acc.cogs;
    return acc;
  }, [fundedRows]);

  const backers = useMemo(() => {
    const grouped = new Map();
    pledges.forEach((pledge) => {
      if (!ELIGIBLE_PLEDGE_STATUSES.has(String(pledge.status || ''))) return;
      const key = String(pledge.backerEmail || '').trim().toLowerCase();
      if (!key) return;
      const current = grouped.get(key) || {
        email: key,
        name: String(pledge.backerName || ''),
        totalUnits: 0,
        totalCommitted: 0,
        latestAt: null,
        latestTiers: new Set(),
      };
      const canonicalTierId = tierAliasMap.get(String(pledge.tierId || '').trim());
      const tier = tiers.find((row) => row.tierId === canonicalTierId);
      const qty = Math.max(1, Number(pledge.qty || 1));
      const wholesaleUnits = Boolean(pledge.isWholesale) ? Math.max(1, Number(tier?.wholesaleUnitCount || 1)) : 1;
      current.totalUnits += qty * wholesaleUnits;
      const cents = Number(pledge.effectivePriceCents || pledge.amountCents || 0);
      current.totalCommitted += cents > 0 ? cents / 100 : Number(tier?.price || 0) * qty;

      const createdMs = new Date(pledge.createdAt || 0).getTime();
      const latestMs = current.latestAt ? new Date(current.latestAt).getTime() : 0;
      if (createdMs >= latestMs) {
        current.latestAt = pledge.createdAt || null;
        if (createdMs > latestMs) current.latestTiers = new Set();
        current.latestTiers.add(String(tier?.tierName || pledge.tierLabel || pledge.tierId || 'Tier'));
      }

      grouped.set(key, current);
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const aTime = new Date(a.latestAt || 0).getTime();
      const bTime = new Date(b.latestAt || 0).getTime();
      return bTime - aTime;
    });
  }, [pledges, tierAliasMap, tiers]);

  const currentForceTier = tiers.find((tier) => tier.tierId === forceModalTierId) || null;
  const currentForceMetrics = currentForceTier ? perTierMetrics.get(currentForceTier.tierId) : null;
  const currentForceMargin = Number(currentForceMetrics?.margin || 0).toFixed(1);

  const runStatus = String(data?.campaign?.status || '').toLowerCase();

  const refresh = async () => {
    await queryClient.invalidateQueries(['drop-run-state', campaignId, brandSlug]);
  };

  const startDrop = async () => {
    try {
      await axios.post(`/api/campaigns/${campaignId}/drop-control`, { action: 'start' });
      await refresh();
      toast.success('Drop started.');
    } catch (error) {
      toast.error(String(error?.response?.data?.error || error?.message || 'Unable to start drop.'));
    }
  };

  const stopDrop = async () => {
    try {
      await axios.post(`/api/campaigns/${campaignId}/drop-control`, { action: 'pause' });
      await refresh();
      toast.success('Drop stopped.');
    } catch (error) {
      toast.error(String(error?.response?.data?.error || error?.message || 'Unable to stop drop.'));
    }
  };

  const openForceModal = (tierId) => {
    setForceDialogError('');
    setForceReason('');
    setForceReasonNote('');
    setForceModalTierId(tierId);
  };

  const closeForceModal = () => {
    if (forceSubmitting) return;
    setForceModalTierId('');
    setForceDialogError('');
    setForceReason('');
    setForceReasonNote('');
  };

  const commitForce = async (tierId, payload = {}) => {
    await forceValidateTier(campaignId, tierId, payload);
    await queryClient.invalidateQueries(['drop-run-state', campaignId]);
  };

  const handleForceClick = async (tier) => {
    const margin = Number(perTierMetrics.get(tier.tierId)?.margin || 0);
    try {
      if (margin >= goMarginPct) {
        await commitForce(tier.tierId);
        toast.success(`${tier.tierName} forced to production.`);
        return;
      }
      openForceModal(tier.tierId);
    } catch (error) {
      toast.error(humanizeForceError(error));
    }
  };

  const handleSubmitForceModal = async () => {
    if (!currentForceTier || !forceReason) return;
    setForceSubmitting(true);
    setForceDialogError('');
    try {
      await commitForce(currentForceTier.tierId, {
        reason: forceReason,
        reasonNote: forceReasonNote.trim().slice(0, 500),
      });
      toast.success(`${currentForceTier.tierName} forced to production.`);
      closeForceModal();
    } catch (error) {
      setForceDialogError(humanizeForceError(error));
    } finally {
      setForceSubmitting(false);
    }
  };

  if (brandSlug === null || isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Skeleton variant="text" width={320} height={48} />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={120} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={120} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={120} /></Grid>
        </Grid>
      </Container>
    );
  }

  if (!brandSlug) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Unable to resolve this brand&apos;s storefront slug. Refresh and try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {showTransientError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Data temporarily unavailable, retrying…
        </Alert>
      ) : null}

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={3}>
        <Box>
          <Typography variant="h4">{data?.campaign?.name || 'Drop Run'}</Typography>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Chip label={data?.brand?.name || 'Brand'} size="small" variant="outlined" />
            <Chip
              label={data?.campaign?.status || 'unknown'}
              color={ACTIVE_RUN_STATUSES.has(runStatus) ? 'success' : 'default'}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
            <Chip
              size="small"
              label={connected ? 'Live updates connected' : 'Live updates disconnected'}
              icon={
                <RadioButtonCheckedIcon
                  sx={{ color: connected ? 'success.main !important' : 'warning.main !important' }}
                />
              }
              variant="outlined"
            />
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => navigate(`/brands/${brandId}/planning/drop`)}>
            Back to Planning
          </Button>
          <Button variant="outlined" color="success" onClick={startDrop} disabled={isFetching}>
            Start Drop
          </Button>
          <Button variant="outlined" color="warning" onClick={stopDrop} disabled={isFetching}>
            Stop Drop
          </Button>
          <Button variant="contained" onClick={refresh} startIcon={<RefreshIcon />} disabled={isFetching}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab value="overview" label="Overview" />
        <Tab value="pnl" label="P&L" />
        <Tab value="whatif" label="What-If" />
      </Tabs>

      {tab === 'overview' ? (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <StatCard title="Backers Pledged" value={data?.totals?.backersPledged || 0} hint="Unique backers with active pledges" />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard title="Units Total" value={data?.totals?.unitsTotal || 0} hint="Authorized + captured units" />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="Tiers Validated"
                value={`${data?.totals?.tiersValidated || 0}/${data?.totals?.totalTiers || 0}`}
                hint="Reservation targets met"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {tiers.map((tier) => (
              <Grid item xs={12} md={6} lg={4} key={tier.tierId}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{tier.tierName}</Typography>
                      <Chip
                        size="small"
                        color={tier.forcedProduction ? 'warning' : tier.validated ? 'success' : 'default'}
                        label={tier.forcedProduction ? 'Forced' : tier.validated ? 'Validated' : 'Open'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {tier.current}/{tier.target} reservations
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, Number(tier.pct || 0)))}
                      sx={{ mt: 1.5, mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatCountdown(tier.deadline)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Pledge Log
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Backer</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pledges.slice(0, 25).map((pledge, index) => (
                      <TableRow key={`${pledge.backerEmail || 'pledge'}-${index}`}>
                        <TableCell>{pledge.backerEmail || '—'}</TableCell>
                        <TableCell>{pledge.tierLabel || pledge.tierId || 'Tier'}</TableCell>
                        <TableCell align="right">{pledge.qty || 1}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={pledge.status || 'unknown'}
                            color={ELIGIBLE_PLEDGE_STATUSES.has(String(pledge.status || '')) ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">{formatDateTime(pledge.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                    {pledges.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No pledges yet.</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Backers
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Total Units</TableCell>
                      <TableCell align="right">Total Committed</TableCell>
                      <TableCell>Most Recent Tier</TableCell>
                      <TableCell align="right">Most Recent Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backers.map((backer) => (
                      <TableRow key={backer.email}>
                        <TableCell>{backer.email}</TableCell>
                        <TableCell>{backer.name || '—'}</TableCell>
                        <TableCell align="right">{backer.totalUnits}</TableCell>
                        <TableCell align="right">{formatCurrency(backer.totalCommitted)}</TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {Array.from(backer.latestTiers).map((tierName) => (
                              <Chip key={`${backer.email}-${tierName}`} size="small" label={tierName} variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatDateTime(backer.latestAt)}</TableCell>
                      </TableRow>
                    ))}
                    {backers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No backers yet.</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {tab === 'pnl' ? (
        <Box>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Validated / Forced Tier P&L
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tier</TableCell>
                      <TableCell align="right">Units</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">COGS</TableCell>
                      <TableCell align="right">GP</TableCell>
                      <TableCell align="right">Margin</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fundedRows.map((row) => {
                      const gp = row.revenue - row.cogs;
                      const margin = row.revenue > 0 ? (gp / row.revenue) * 100 : 0;
                      return (
                        <TableRow key={row.tier.tierId}>
                          <TableCell>{row.tier.tierName}</TableCell>
                          <TableCell align="right">{row.units}</TableCell>
                          <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.cogs)}</TableCell>
                          <TableCell align="right">{formatCurrency(gp)}</TableCell>
                          <TableCell align="right">{margin.toFixed(1)}%</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell><strong>Grand Total</strong></TableCell>
                      <TableCell align="right">
                        <strong>{fundedRows.reduce((sum, row) => sum + row.units, 0)}</strong>
                      </TableCell>
                      <TableCell align="right"><strong>{formatCurrency(totals.revenue)}</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(totals.cogs)}</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(totals.gp)}</strong></TableCell>
                      <TableCell align="right">
                        <strong>{totals.revenue > 0 ? ((totals.gp / totals.revenue) * 100).toFixed(1) : '0.0'}%</strong>
                      </TableCell>
                    </TableRow>
                    {fundedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No validated or forced tiers yet.</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {data?.totals?.totalCarbonKgCommitted != null || data?.totals?.totalCarbonKgAvoided != null ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Sustainability Impact</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Carbon committed: {data?.totals?.totalCarbonKgCommitted ?? '—'} kg
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Carbon avoided: {data?.totals?.totalCarbonKgAvoided ?? '—'} kg
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </Box>
      ) : null}

      {tab === 'whatif' ? (
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Unfunded Tiers</Typography>
              <Typography variant="body2" color="text.secondary">
                GO: ≥ {goMarginPct}% • REVIEW: {reviewMarginPct}% - {goMarginPct - 0.01}% • NO GO: &lt; {reviewMarginPct}%
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tier</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">COGS</TableCell>
                    <TableCell align="right">Margin</TableCell>
                    <TableCell>Verdict</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {whatIfRows.map((row) => {
                    const verdictResult = computeVerdict({
                      margin: row.margin,
                      revenue: row.revenue,
                      carbonKg: row.carbonKg,
                      goMarginPct,
                      reviewMarginPct,
                    });
                    const tier = row.tier;
                    return (
                      <TableRow key={tier.tierId}>
                        <TableCell>{tier.tierName}</TableCell>
                        <TableCell align="right">{row.units}</TableCell>
                        <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.cogs)}</TableCell>
                        <TableCell align="right">{row.margin.toFixed(1)}%</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              size="small"
                              label={verdictResult.verdict}
                              color={
                                verdictResult.verdict === 'GO'
                                  ? 'success'
                                  : verdictResult.verdict === 'REVIEW'
                                  ? 'warning'
                                  : 'error'
                              }
                            />
                            {verdictResult.co2Downgraded ? (
                              <Chip size="small" variant="outlined" color="warning" label="↓ CO₂ penalty" />
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Button variant="contained" onClick={() => handleForceClick(tier)}>
                            Force Produce
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {whatIfRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No unfunded tiers with active units.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={Boolean(currentForceTier)} onClose={closeForceModal} fullWidth maxWidth="sm">
        <DialogTitle>Force-produce {currentForceTier?.tierName || 'tier'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This tier is at {currentForceMargin}% margin, below your brand&apos;s GO threshold of {goMarginPct}%.
            Producing it anyway requires a reason for the audit log.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="force-reason-label">Reason</InputLabel>
            <Select
              labelId="force-reason-label"
              label="Reason"
              value={forceReason}
              onChange={(event) => setForceReason(event.target.value)}
            >
              {FORCE_REASON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason note (optional)"
            value={forceReasonNote}
            inputProps={{ maxLength: 500 }}
            onChange={(event) => setForceReasonNote(event.target.value)}
          />
          {forceDialogError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {forceDialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForceModal} disabled={forceSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitForceModal}
            disabled={!forceReason || forceSubmitting}
          >
            Commit to production
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DropDashboardPage;
